"use client";

import { useRef, useEffect } from "react";

interface UseForestAudioReturn {
  start: () => void;
  stop: () => void;
  setMuted: (muted: boolean) => void;
  setBrookVolume: (volume: number) => void;
  playDoorCreak: () => void;
}

const BIRDS_SRC = "/audio/forest-birds.mp3";
const BIRDS_VOLUME = 0.22;

const STEPS_BASE_VOLUME = 0.07;
// The brook track is synthesized (shaped noise) — above a whisper it reads as
// white-noise hiss and masks the real bird recording. Keep it WELL under the
// birds (0.22); distance attenuation in ForestAudioManager scales it further.
const BROOK_BASE_VOLUME = 0.06;

const LAYERS = [
  { src: "/audio/forest-steps.mp3", volume: STEPS_BASE_VOLUME },
  { src: "/audio/forest-brook.mp3", volume: BROOK_BASE_VOLUME },
];

const useForestAudio = (): UseForestAudioReturn => {
  const audioRefs = useRef<HTMLAudioElement[]>([]);
  const startedRef = useRef(false);
  const brookVolumeRef = useRef(1); // spatial attenuation multiplier (0..1)

  // Birds: an end-to-end loop of the field recording is always audible — even
  // a sample-accurate Web Audio loop "starts over" to the ear, because the
  // same distinctive calls recur in the same order every ~89s. So instead of
  // looping, we schedule randomized SLICES of the recording (16–26s each),
  // overlapped with equal-power crossfades. The sequence never repeats, so
  // there is no loop point to hear.
  const ctxRef = useRef<AudioContext | null>(null);
  const birdsBufferRef = useRef<AudioBuffer | null>(null);
  const birdsGainRef = useRef<GainNode | null>(null);
  const birdsSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const birdsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const birdsLastOffsetRef = useRef(-100);

  const SLICE_MIN = 16;
  const SLICE_MAX = 26;
  const XFADE = 2.5; // crossfade overlap between consecutive slices
  const EDGE = 1.0; // skip the buffer's first/last second (MP3 padding)

  // Equal-power fade curves — a linear crossfade of two uncorrelated ambience
  // slices dips audibly in the middle; sin/cos quarter curves hold loudness.
  const FADE_STEPS = 24;
  const fadeIn = new Float32Array(FADE_STEPS).map((_, i) =>
    Math.sin(((i / (FADE_STEPS - 1)) * Math.PI) / 2)
  );
  const fadeOut = new Float32Array(FADE_STEPS).map((_, i) =>
    Math.cos(((i / (FADE_STEPS - 1)) * Math.PI) / 2)
  );

  function stopBirds() {
    if (birdsTimerRef.current !== null) {
      clearTimeout(birdsTimerRef.current);
      birdsTimerRef.current = null;
    }
    for (const source of birdsSourcesRef.current) {
      try {
        source.stop();
      } catch {
        // Already stopped.
      }
      source.disconnect();
    }
    birdsSourcesRef.current.clear();
  }

  // Schedules one slice to begin (fade in) at `when`, and chains a timer to
  // schedule the next slice so its fade-in overlaps this slice's fade-out.
  function scheduleSlice(when: number) {
    const ctx = ctxRef.current;
    const buffer = birdsBufferRef.current;
    const gain = birdsGainRef.current;
    if (!ctx || !buffer || !gain || !startedRef.current) return;

    const rnd = () => Math.random();
    const dur = SLICE_MIN + rnd() * (SLICE_MAX - SLICE_MIN);
    const maxStart = buffer.duration - EDGE - dur;
    let offset = EDGE + rnd() * Math.max(0, maxStart - EDGE);
    // Re-roll once if we landed near the previous slice's start — back-to-back
    // near-identical material is the one thing that could still sound loopy.
    if (Math.abs(offset - birdsLastOffsetRef.current) < 8) {
      offset = EDGE + rnd() * Math.max(0, maxStart - EDGE);
    }
    birdsLastOffsetRef.current = offset;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const sliceGain = ctx.createGain();
    sliceGain.gain.setValueAtTime(0, when);
    sliceGain.gain.setValueCurveAtTime(fadeIn, when, XFADE);
    sliceGain.gain.setValueAtTime(1, when + XFADE);
    sliceGain.gain.setValueCurveAtTime(fadeOut, when + dur - XFADE, XFADE);
    source.connect(sliceGain).connect(gain);
    source.start(when, offset, dur + 0.05);

    birdsSourcesRef.current.add(source);
    source.onended = () => {
      birdsSourcesRef.current.delete(source);
      source.disconnect();
      sliceGain.disconnect();
    };

    // Next slice starts where this one begins fading out. Wake 1s early so
    // scheduling stays sample-accurate even if the timer fires late.
    const nextWhen = when + dur - XFADE;
    const delayMs = Math.max(0, (nextWhen - ctx.currentTime - 1.0) * 1000);
    birdsTimerRef.current = setTimeout(() => scheduleSlice(nextWhen), delayMs);
  }

  function startBirds() {
    const ctx = ctxRef.current;
    const buffer = birdsBufferRef.current;
    const gain = birdsGainRef.current;
    if (!ctx || !buffer || !gain || birdsSourcesRef.current.size > 0) return;

    // Resume context in case it is suspended before the user gesture.
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }

    scheduleSlice(ctx.currentTime + 0.05);
  }

  // Create HTMLAudio elements for steps/brook and decode birds into an
  // AudioBuffer ahead of time so the first start is gapless.
  useEffect(() => {
    const audios: HTMLAudioElement[] = [];
    for (const layer of LAYERS) {
      const a = document.createElement("audio");
      a.src = layer.src;
      a.loop = true;
      a.preload = "auto";
      a.volume = layer.volume;
      a.style.display = "none";
      document.body.appendChild(a);
      audios.push(a);
    }
    audioRefs.current = audios;

    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      ctxRef.current = ctx;
      const gain = ctx.createGain();
      gain.gain.value = BIRDS_VOLUME;
      gain.connect(ctx.destination);
      birdsGainRef.current = gain;

      fetch(BIRDS_SRC)
        .then((res) => res.arrayBuffer())
        .then((arrayBuffer) => ctx.decodeAudioData(arrayBuffer))
        .then((buffer) => {
          birdsBufferRef.current = buffer;
          // If start() was called while we were still decoding, kick off birds now.
          if (startedRef.current) {
            startBirds();
          }
        })
        .catch(() => {
          // If decoding fails, birds simply won't play; other layers still work.
        });
    }

    return () => {
      for (const a of audios) {
        a.pause();
        a.remove();
      }
      audioRefs.current = [];
      stopBirds();
      ctxRef.current?.close();
      ctxRef.current = null;
      birdsBufferRef.current = null;
      birdsGainRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startBirds/stopBirds read only stable refs; this is a mount-once effect
  }, []);

  function applyBrookVolume() {
    const brook = audioRefs.current[1];
    if (brook) {
      brook.volume = BROOK_BASE_VOLUME * brookVolumeRef.current;
    }
  }

  function start() {
    if (startedRef.current) return;
    startedRef.current = true;

    for (const a of audioRefs.current) {
      a.play().catch(() => {
        // Browsers block autoplay until user gesture — this is expected
        // on the first call. The gesture handler will retry.
      });
    }
    applyBrookVolume();

    startBirds();
  }

  function stop() {
    startedRef.current = false;
    for (const a of audioRefs.current) {
      a.pause();
      // eslint-disable-next-line react-hooks/immutability -- imperative control of a non-React-managed <audio> element; same precedent as ParticleField's uniform writes
      a.currentTime = 0;
    }
    stopBirds();
  }

  function setMuted(muted: boolean) {
    for (const a of audioRefs.current) {
      // eslint-disable-next-line react-hooks/immutability -- imperative control of a non-React-managed <audio> element
      a.muted = muted;
    }
    if (birdsGainRef.current) {
      birdsGainRef.current.gain.value = muted ? 0 : BIRDS_VOLUME;
    }
  }

  function setBrookVolume(volume: number) {
    // volume is expected in 0..1 (attenuation multiplier)
    brookVolumeRef.current = Math.max(0, Math.min(1, volume));
    applyBrookVolume();
  }

  /**
   * Procedural wooden door creak (Web Audio). Synced to the door's 2.8s swing
   * (DOOR_SWING_SECONDS in WorkshopDoor): a low wood groan + irregular
   * stick-slip squeak grains whose intensity tracks the door's velocity (peak
   * mid-swing) + a wood-grain noise bed + a soft settle thud as it comes to
   * rest. To change character, swap the V params below (heavy / rusty / light
   * are the previewed presets).
   */
  function playDoorCreak() {
    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx: AudioContext = new AudioCtx();
    const t = ctx.currentTime;

    // "heavy" preset — deep wooden groan + creak for the strapped workshop door.
    const V = { dur: 2.8, master: 0.95, groan: 0.16, sqStart: 230, sqEnd: 760, q: 7, gapMin: 0.05, gapMax: 0.14, sqLvl: 0.16, noise: 0.05, thunk: 0.22 };
    const dur = V.dur;
    const rnd = () => Math.random();

    const master = ctx.createGain();
    master.gain.value = V.master;
    master.connect(ctx.destination);

    // 1) Low wood groan — detuned saws through a lowpass, swells then fades.
    const groanGain = ctx.createGain();
    groanGain.gain.setValueAtTime(0.0001, t);
    groanGain.gain.linearRampToValueAtTime(V.groan, t + 0.3);
    groanGain.gain.setValueAtTime(V.groan, t + dur * 0.6);
    groanGain.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    const groanLP = ctx.createBiquadFilter();
    groanLP.type = "lowpass";
    groanLP.frequency.value = 300;
    groanLP.connect(groanGain).connect(master);
    [0, 1].forEach((i) => {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(64 + i * 4, t);
      o.frequency.linearRampToValueAtTime(86 + i * 4, t + dur * 0.5);
      o.frequency.linearRampToValueAtTime(58 + i * 3, t + dur);
      o.connect(groanLP);
      o.start(t);
      o.stop(t + dur + 0.05);
    });

    // 2) Stick-slip squeak — saw through a resonant bandpass, gated into
    //    irregular grains with ratcheting pitch. This is the "creeeak".
    const sqOsc = ctx.createOscillator();
    sqOsc.type = "sawtooth";
    sqOsc.frequency.setValueAtTime(V.sqStart, t);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = V.q;
    bp.frequency.setValueAtTime(V.sqStart * 2.2, t);
    bp.frequency.linearRampToValueAtTime(V.sqEnd * 2.2, t + dur * 0.85);
    const sqGain = ctx.createGain();
    sqGain.gain.setValueAtTime(0.0001, t);
    sqOsc.connect(bp).connect(sqGain).connect(master);
    let tt = t + 0.06;
    while (tt < t + dur * 0.9) {
      const gap = V.gapMin + rnd() * (V.gapMax - V.gapMin);
      const prog = (tt - t) / dur;
      const vel = Math.sin(Math.min(1, prog) * Math.PI); // door velocity ~ peaks mid-swing
      const lvl = Math.max(0.0008, V.sqLvl * (0.35 + 0.65 * vel) * (0.6 + rnd() * 0.7));
      sqGain.gain.setValueAtTime(Math.max(0.0008, sqGain.gain.value), tt);
      sqGain.gain.linearRampToValueAtTime(lvl, tt + gap * 0.25);
      sqGain.gain.exponentialRampToValueAtTime(0.0008, tt + gap * 0.95);
      const f = V.sqStart + (V.sqEnd - V.sqStart) * prog * (0.8 + rnd() * 0.45);
      sqOsc.frequency.setValueAtTime(f, tt);
      tt += gap;
    }
    sqOsc.start(t);
    sqOsc.stop(t + dur + 0.05);

    // 3) Wood-grain noise bed, bandpassed, follows the groan envelope.
    const nbuf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * (dur + 0.3)), ctx.sampleRate);
    const nd = nbuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = rnd() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = nbuf;
    const nf = ctx.createBiquadFilter();
    nf.type = "bandpass";
    nf.frequency.value = 420;
    nf.Q.value = 0.8;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t);
    ng.gain.linearRampToValueAtTime(V.noise, t + 0.3);
    ng.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    noise.connect(nf).connect(ng).connect(master);
    noise.start(t);
    noise.stop(t + dur + 0.05);

    // 4) Settle thud — soft low thump as the door comes to rest.
    const thG = ctx.createGain();
    thG.gain.setValueAtTime(0.0001, t + dur * 0.86);
    thG.gain.linearRampToValueAtTime(V.thunk, t + dur * 0.9);
    thG.gain.exponentialRampToValueAtTime(0.0008, t + dur + 0.28);
    const thO = ctx.createOscillator();
    thO.type = "sine";
    thO.frequency.setValueAtTime(72, t + dur * 0.86);
    thO.frequency.exponentialRampToValueAtTime(44, t + dur + 0.22);
    thO.connect(thG).connect(master);
    thO.start(t + dur * 0.86);
    thO.stop(t + dur + 0.3);

    // Cleanup context after playback
    setTimeout(() => ctx.close(), (dur + 0.6) * 1000);
  }

  return { start, stop, setMuted, setBrookVolume, playDoorCreak };
};

export default useForestAudio;
